import { StatusBadge, type Tom } from "@/components/data/status-badge";
import { cn } from "@/lib/utils";
import { ENTREGA_AVISO_ROTULO, type AvisoEntrega, type AvisoFilaStatus, type AvisoPublico } from "@shared/avisos";
import { ROTULO_STATUS_AVISO } from "@shared/avisos-admin";

// Peças visuais dos avisos pelo WhatsApp — a bolha da prévia e os selos das
// listas. A bolha imita o WhatsApp de propósito: é assim que a pessoa vai ler, e
// a quebra de linha é conteúdo (a lista do grupo, o link na linha própria).

/** Links do texto em azul e sublinhados, como o WhatsApp mostra. */
function comLinks(texto: string) {
  const partes = texto.split(/(https?:\/\/\S+)/g);
  return partes.map((p, i) => (/^https?:\/\//.test(p)
    ? <span key={i} className="text-[#027EB5] underline break-all">{p}</span>
    : <span key={i}>{p}</span>));
}

export function BolhaWhatsApp({ texto, hora = "07:02", testId }: { texto: string; hora?: string; testId?: string }) {
  return (
    <div
      className="max-w-[340px] rounded-[10px] rounded-bl-[3px] bg-white px-2.5 pt-2 pb-1.5 text-[13px] leading-[1.45] text-[#111B21] shadow-[0_1px_1px_rgba(0,0,0,0.08)] whitespace-pre-wrap break-words dark:bg-[#202C33] dark:text-[#E9EDEF]"
      data-testid={testId}
    >
      {comLinks(texto)}
      <span className="block text-right text-[10.5px] text-[#667781] mt-0.5">{hora}</span>
    </div>
  );
}

/** O fundo da conversa, com o "remetente" em cima. */
export function ConversaWhatsApp({ para, children, className }: { para?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
        <span className="relative inline-block h-6 w-6 rounded-full bg-[#1F5A26]" aria-hidden>
          <span className="absolute right-1.5 bottom-1.5 h-1 w-1 rounded-full bg-[#A8C63C]" />
        </span>
        Rizoma{para && <span className="font-normal">· {para}</span>}
      </div>
      <div className="grid gap-2 rounded-xl bg-[#EFE7DC] p-3.5 dark:bg-[#0B141A]">{children}</div>
    </div>
  );
}

export function SeloPublico({ publico }: { publico: AvisoPublico }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {publico !== "COMPRADOR" && <StatusBadge tom="sucesso">Produtor</StatusBadge>}
      {publico !== "PRODUTOR" && <StatusBadge tom="info">Comprador</StatusBadge>}
    </span>
  );
}

const TOM_ENTREGA: Record<AvisoEntrega, Tom> = {
  IMEDIATO: "atencao",
  AGRUPADO: "info",
  LEMBRETE: "neutro",
  RESUMO: "neutro",
};

export function SeloEntrega({ entrega, janelaMin }: { entrega: AvisoEntrega; janelaMin?: number }) {
  const rotulo = entrega === "AGRUPADO" && janelaMin ? `Agrupado ${janelaMin} min` : ENTREGA_AVISO_ROTULO[entrega];
  return <StatusBadge tom={TOM_ENTREGA[entrega]}>{rotulo}</StatusBadge>;
}

const TOM_STATUS: Record<AvisoFilaStatus, Tom> = {
  PENDENTE: "info",
  ENVIADA: "sucesso",
  AGRUPADA: "sucesso",
  SEGURADA: "atencao",
  SUPRIMIDA: "neutro",
  CANCELADA: "neutro",
  FALHOU: "erro",
};

export function SeloStatusEnvio({ status, testId }: { status: AvisoFilaStatus; testId?: string }) {
  return <StatusBadge tom={TOM_STATUS[status]} ponto testId={testId}>{ROTULO_STATUS_AVISO[status]}</StatusBadge>;
}
